import { useCallback, useEffect, useMemo, useState } from "react";
import { differenceInCalendarDays, format, subDays } from "date-fns";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import type { Deal } from "../../lib/types";
import { notifyShannonOnSupplementRelease } from "../../lib/notifyShannonOnSupplementRelease";
import DealLink from "../DealLink";
import DealCallText from "../DealCallText";
import RouteMap from "../shared/RouteMap";
import type { RouteStop } from "../shared/RouteMap";
import { formatPhone } from "../../lib/phoneFormat";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Chad's Build Follow-ups Tab — Post-build follow-up route builder.
 * Shows recent completed builds (last 14 days). Chad selects which ones
 * to visit today, builds a route, marks visited, adds punch list notes.
 *
 * Ship 2 (May 2, 2026 — Finance Board Redesign), updated May 7:
 * Each card with stage='ready_for_supps' carries a supplement-notes textarea
 * + Notify Shannon button. The deal already lands at ready_for_supps when
 * Chad marks the build complete (TransitionButton + dispatchActions both
 * route there directly now — roof_complete stage retired). Notify Shannon
 * here is a "I've finished my supp notes" handoff: saves notes, stamps
 * chad_supp_notes_due_at (72h SLA reset), fires notifyShannonOnSupplementRelease.
 */

const COLS =
  "id,homeowner_name,homeowner_phone,property_street,property_city,property_state,property_zip,property_lat,property_lon,claim_number,stage,phase,owner_id,split_rm_id,build_completed_date,build_scheduled_date,shingle_color,drip_edge_color,trades_to_perform,insurance_company,acv_amount,chad_supplement_notes,chad_supp_notes_due_at,build_followup_visited_at";

type RecentBuild = Deal & {
  daysAgo: number;
  rmName: string;
  punchItems: number;
  punchNote: string;
  tradesComplete: boolean;
};

function formatAddr(d: Deal) {
  return [d.property_street, [d.property_city, d.property_state].filter(Boolean).join(", "), d.property_zip].filter(Boolean).join(" ");
}

export default function ChadFollowupsTab() {
  const [builds, setBuilds] = useState<RecentBuild[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Route state
  const [inRoute, setInRoute] = useState<Set<string>>(new Set());
  const [visited, setVisited] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [punchNotes, setPunchNotes] = useState<Record<string, string>>({});

  // Punch note modal
  const [punchModalFor, setPunchModalFor] = useState<RecentBuild | null>(null);
  const [punchInput, setPunchInput] = useState("");
  const [punchResponsibility, setPunchResponsibility] = useState("Bardin pays");

  // Ship 2: supplement-notes editing buffer + release-in-flight tracking
  const [suppNotesDraft, setSuppNotesDraft] = useState<Record<string, string>>({});
  const [releasingId, setReleasingId] = useState<string | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("deals", reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      const today = new Date();
      const twoWeeksAgo = format(subDays(today, 14), "yyyy-MM-dd");

      // Get builds completed in the last 14 days
      const { data, error: err } = await supabase
        .from("deals")
        .select(COLS)
        .not("build_completed_date", "is", null)
        .gte("build_completed_date", twoWeeksAgo)
        .order("build_completed_date", { ascending: false });

      if (err) {
        if (!cancelled) setError(err.message);
        return;
      }

      // Also get builds that are in closing phase but recently built
      const { data: closingBuilds } = await supabase
        .from("deals")
        .select(COLS)
        .eq("phase", "closing")
        .not("build_scheduled_date", "is", null)
        .gte("build_scheduled_date", twoWeeksAgo)
        .is("build_completed_date", null);

      const allDeals = [...(data ?? []), ...(closingBuilds ?? [])];

      // Dedupe by ID
      const seen = new Set<string>();
      const unique = allDeals.filter((d) => {
        if (seen.has(d.id)) return false;
        seen.add(d.id);
        return true;
      });

      // Resolve RM names
      const ownerIds = new Set<string>();
      unique.forEach((d) => { if (d.owner_id) ownerIds.add(d.owner_id); });
      const memberMap: Record<string, string> = {};
      if (ownerIds.size > 0) {
        const { data: ms } = await supabase.from("team_roster").select("id,full_name").in("id", [...ownerIds]);
        (ms ?? []).forEach((m) => { memberMap[m.id] = m.full_name; });
      }

      const results: RecentBuild[] = unique.map((d) => {
        const builtDate = d.build_completed_date ?? d.build_scheduled_date;
        const daysAgo = builtDate
          ? differenceInCalendarDays(today, new Date(builtDate))
          : 0;
        const trades = d.trades_to_perform ?? [];
        return {
          ...(d as unknown as Deal),
          daysAgo,
          rmName: d.owner_id ? memberMap[d.owner_id] ?? "—" : "—",
          punchItems: 0, // Would come from a punch_list table in production
          punchNote: "",
          tradesComplete: trades.length === 0,
        };
      });

      results.sort((a, b) => a.daysAgo - b.daysAgo);
      if (!cancelled) {
        setBuilds(results);
        // Visited is now persisted on the deal (build_followup_visited_at).
        // Re-derive the visited Set from the DB on every load so it survives
        // a refresh and stays consistent across realtime reloads.
        setVisited(new Set(results.filter((r) => r.build_followup_visited_at).map((r) => r.id)));
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Route map stops
  const routeStops: RouteStop[] = useMemo(() => {
    if (!builds) return [];
    return builds
      .filter((b) => inRoute.has(b.id) && !visited.has(b.id))
      .filter((b) => b.property_lat != null && b.property_lon != null)
      .map((b, i) => ({
        label: String(i + 1),
        name: `Follow-up — ${b.homeowner_name ?? "(no name)"}`,
        address: formatAddr(b),
        lat: b.property_lat!,
        lon: b.property_lon!,
      }));
  }, [builds, inRoute, visited]);

  // Google Maps route link
  const googleRouteLink = useMemo(() => {
    if (routeStops.length === 0) return "";
    const coords = routeStops.map((s) => `${s.lat},${s.lon}`);
    if (coords.length === 1)
      return `https://www.google.com/maps/dir/?api=1&destination=${coords[0]}&travelmode=driving`;
    const origin = coords[0];
    const dest = coords[coords.length - 1];
    const waypoints = coords.slice(1, -1).join("/");
    return waypoints
      ? `https://www.google.com/maps/dir/${origin}/${waypoints}/${dest}`
      : `https://www.google.com/maps/dir/${origin}/${dest}`;
  }, [routeStops]);

  const addToRoute = useCallback((id: string) => {
    setInRoute((prev) => new Set(prev).add(id));
    setExpanded(null);
  }, []);

  const removeFromRoute = useCallback((id: string) => {
    setInRoute((prev) => { const n = new Set(prev); n.delete(id); return n; });
    setExpanded(null);
  }, []);

  // Persist visited to the deal so it survives a refresh (permanent log).
  // Optimistic local add; realtime reload re-derives from the DB column.
  const markDone = useCallback(async (id: string) => {
    setVisited((prev) => new Set(prev).add(id));
    setExpanded(null);
    const stamp = new Date().toISOString();
    const { error } = await supabase
      .from("deals")
      .update({ build_followup_visited_at: stamp })
      .eq("id", id);
    if (error) {
      // Roll back the optimistic add so the card doesn't look done when it isn't.
      setVisited((prev) => { const n = new Set(prev); n.delete(id); return n; });
      alert(`Couldn't mark visited: ${error.message}`);
      return;
    }
    setBuilds((prev) =>
      prev?.map((b) => (b.id === id ? { ...b, build_followup_visited_at: stamp } : b)) ?? null,
    );
  }, []);

  // Undo a visited mark (misclick) — clears the timestamp and returns the
  // build to the available list.
  const markUndone = useCallback(async (id: string) => {
    setVisited((prev) => { const n = new Set(prev); n.delete(id); return n; });
    const { error } = await supabase
      .from("deals")
      .update({ build_followup_visited_at: null })
      .eq("id", id);
    if (error) {
      setVisited((prev) => new Set(prev).add(id));
      alert(`Couldn't undo: ${error.message}`);
      return;
    }
    setBuilds((prev) =>
      prev?.map((b) => (b.id === id ? { ...b, build_followup_visited_at: null } : b)) ?? null,
    );
  }, []);

  const openPunchModal = useCallback((b: RecentBuild) => {
    setPunchModalFor(b);
    setPunchInput(punchNotes[b.id] ?? b.punchNote ?? "");
  }, [punchNotes]);

  const savePunchNote = useCallback(() => {
    if (!punchModalFor) return;
    setPunchNotes((prev) => ({ ...prev, [punchModalFor.id]: punchInput }));
    // In production: save to Supabase punch_list or activity_log
    setPunchModalFor(null);
    setPunchInput("");
  }, [punchModalFor, punchInput]);

  // Ship 2: edit-buffer the textarea so typing doesn't trigger a Supabase round-trip per keystroke
  const setSuppNotes = useCallback((dealId: string, value: string) => {
    setSuppNotesDraft((prev) => ({ ...prev, [dealId]: value }));
  }, []);

  // Save Chad's supplement notes to the deal (no stage transition yet — that's Release)
  const saveSuppNotes = useCallback(async (dealId: string) => {
    const draft = suppNotesDraft[dealId];
    if (!draft || draft.trim().length === 0) return;
    const { error } = await supabase
      .from("deals")
      .update({ chad_supplement_notes: draft.trim() })
      .eq("id", dealId);
    if (error) {
      alert(`Couldn't save notes: ${error.message}`);
      return;
    }
    // Reflect locally; reload via realtime channel will pick it up too
    setBuilds((prev) =>
      prev?.map((b) => (b.id === dealId ? { ...b, chad_supplement_notes: draft.trim() } : b)) ?? null,
    );
    setSuppNotesDraft((prev) => { const n = { ...prev }; delete n[dealId]; return n; });
  }, [suppNotesDraft]);

  // Notify Shannon — saves notes, stamps the 72h SLA timer, fires
  // notifyShannonOnSupplementRelease. The deal is already at ready_for_supps
  // (transition happens at build-complete time now), so no stage update.
  // Requires non-empty notes (≥10 chars) — Greg's rule per spec §10b.
  const releaseToShannon = useCallback(async (deal: RecentBuild) => {
    const notes = (suppNotesDraft[deal.id] ?? deal.chad_supplement_notes ?? "").trim();
    if (notes.length < 10) {
      alert("Add at least 10 characters of supplement notes before releasing.");
      return;
    }
    setReleasingId(deal.id);
    try {
      const dueAt = new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from("deals")
        .update({
          chad_supplement_notes: notes,
          chad_supp_notes_due_at: dueAt,
        })
        .eq("id", deal.id);
      if (error) {
        alert(`Couldn't notify Shannon: ${error.message}`);
        return;
      }
      // Fire-and-forget — Shannon's SMS + in-portal notification.
      // Re-pinging Shannon here is intentional: she got an initial alert when
      // the deal hit ready_for_supps, this is "Chad's finished his notes."
      notifyShannonOnSupplementRelease({
        ...deal,
        chad_supplement_notes: notes,
      });
      setBuilds((prev) =>
        prev?.map((b) => (b.id === deal.id
          ? { ...b, chad_supplement_notes: notes, chad_supp_notes_due_at: dueAt }
          : b)) ?? null,
      );
      setSuppNotesDraft((prev) => { const n = { ...prev }; delete n[deal.id]; return n; });
    } finally {
      setReleasingId(null);
    }
  }, [suppNotesDraft]);

  if (error)
    return <div className="bg-red-50 text-red-700 p-4 rounded">Couldn't load follow-ups — {error}</div>;
  if (!builds)
    return <div className="text-slate-500 p-4">Loading recent builds…</div>;

  const routeBuilds = builds.filter((b) => inRoute.has(b.id) && !visited.has(b.id));
  const visitedBuilds = builds.filter((b) => visited.has(b.id));
  const availableBuilds = builds.filter((b) => !inRoute.has(b.id) && !visited.has(b.id));
  const hasPunch = availableBuilds.filter((b) => (punchNotes[b.id] ?? b.punchNote) || !b.tradesComplete);
  const standard = availableBuilds.filter((b) => !(punchNotes[b.id] ?? b.punchNote) && b.tradesComplete);

  return (
    <div>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Recent Builds" value={builds.length} />
        <KPI label="In Today's Route" value={routeBuilds.length} color="text-teal-600" />
        <KPI label="Visited" value={visitedBuilds.length} color="text-emerald-600" />
        <KPI
          label="Needs Attention"
          value={hasPunch.length}
          color={hasPunch.length > 0 ? "text-red-600" : undefined}
        />
      </div>

      {/* Route map */}
      {routeStops.length > 0 ? (
        <div className="mb-4">
          <RouteMap stops={routeStops} minutesPerStop={10} perStopLabel="per visit" />
          {googleRouteLink && (
            <div className="flex justify-end mt-2">
              <a
                href={googleRouteLink}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 flex items-center gap-1.5"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/></svg>
                Open Route ({routeStops.length} stops)
              </a>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-4 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
          <div className="text-2xl mb-1">🗺️</div>
          <div className="font-medium">No builds in today's route yet</div>
          <div className="text-xs mt-1">Select builds below to plan your follow-up visits</div>
        </div>
      )}

      {/* MY ROUTE */}
      {routeBuilds.length > 0 && (
        <div className="bg-teal-50 border-2 border-teal-300 rounded-xl p-4 mb-4">
          <h3 className="text-sm font-bold text-teal-700 mb-3">
            🚗 Today's Follow-up Route — {routeBuilds.length} stop{routeBuilds.length > 1 ? "s" : ""}
          </h3>
          {routeBuilds.map((b, i) => (
            <FollowupCard
              key={b.id}
              build={b}
              routeNum={i + 1}
              mode="route"
              expanded={expanded === b.id}
              onToggleExpand={() => setExpanded(expanded === b.id ? null : b.id)}
              onAddToRoute={() => {}}
              onRemoveFromRoute={() => removeFromRoute(b.id)}
              onMarkDone={() => markDone(b.id)}
              onOpenPunch={() => openPunchModal(b)}
              punchNote={punchNotes[b.id] ?? b.punchNote}
              suppNotesDraft={suppNotesDraft[b.id]}
              onSetSuppNotes={(v) => setSuppNotes(b.id, v)}
              onSaveSuppNotes={() => saveSuppNotes(b.id)}
              onReleaseToShannon={() => releaseToShannon(b)}
              releasing={releasingId === b.id}
            />
          ))}
        </div>
      )}

      {/* AVAILABLE BUILDS */}
      {availableBuilds.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4 mb-4">
          <h3 className="text-sm font-bold text-bardin-ink mb-1">
            🏠 Recent Builds — {availableBuilds.length} available
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            Click "Add to Route" to plan follow-up visits. Builds needing attention shown first.
          </p>

          {hasPunch.length > 0 && (
            <>
              <div className="text-xs font-bold text-red-600 uppercase tracking-wide mb-2 pb-1 border-b border-red-100">
                ⚠️ Needs Attention ({hasPunch.length})
              </div>
              {hasPunch.map((b) => (
                <FollowupCard
                  key={b.id}
                  build={b}
                  mode="available"
                  expanded={expanded === b.id}
                  onToggleExpand={() => setExpanded(expanded === b.id ? null : b.id)}
                  onAddToRoute={() => addToRoute(b.id)}
                  onRemoveFromRoute={() => {}}
                  onMarkDone={() => {}}
                  onOpenPunch={() => openPunchModal(b)}
                  punchNote={punchNotes[b.id] ?? b.punchNote}
                  suppNotesDraft={suppNotesDraft[b.id]}
                  onSetSuppNotes={(v) => setSuppNotes(b.id, v)}
                  onSaveSuppNotes={() => saveSuppNotes(b.id)}
                  onReleaseToShannon={() => releaseToShannon(b)}
                  releasing={releasingId === b.id}
                />
              ))}
            </>
          )}
          {standard.length > 0 && (
            <>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-3 mb-2 pb-1 border-b border-slate-100">
                Standard Follow-up ({standard.length})
              </div>
              {standard.map((b) => (
                <FollowupCard
                  key={b.id}
                  build={b}
                  mode="available"
                  expanded={expanded === b.id}
                  onToggleExpand={() => setExpanded(expanded === b.id ? null : b.id)}
                  onAddToRoute={() => addToRoute(b.id)}
                  onRemoveFromRoute={() => {}}
                  onMarkDone={() => {}}
                  onOpenPunch={() => openPunchModal(b)}
                  punchNote={punchNotes[b.id] ?? b.punchNote}
                  suppNotesDraft={suppNotesDraft[b.id]}
                  onSetSuppNotes={(v) => setSuppNotes(b.id, v)}
                  onSaveSuppNotes={() => saveSuppNotes(b.id)}
                  onReleaseToShannon={() => releaseToShannon(b)}
                  releasing={releasingId === b.id}
                />
              ))}
            </>
          )}
        </div>
      )}

      {/* VISITED TODAY */}
      {visitedBuilds.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4 opacity-70">
          <h3 className="text-sm font-bold text-emerald-600 mb-3">
            ✅ Visited Today ({visitedBuilds.length})
          </h3>
          {visitedBuilds.map((b) => (
            <FollowupCard
              key={b.id}
              build={b}
              mode="visited"
              expanded={expanded === b.id}
              onToggleExpand={() => setExpanded(expanded === b.id ? null : b.id)}
              onAddToRoute={() => {}}
              onRemoveFromRoute={() => {}}
              onMarkDone={() => {}}
              onUndo={() => markUndone(b.id)}
              onOpenPunch={() => openPunchModal(b)}
              punchNote={punchNotes[b.id] ?? b.punchNote}
              suppNotesDraft={suppNotesDraft[b.id]}
              onSetSuppNotes={(v) => setSuppNotes(b.id, v)}
              onSaveSuppNotes={() => saveSuppNotes(b.id)}
              onReleaseToShannon={() => releaseToShannon(b)}
              releasing={releasingId === b.id}
            />
          ))}
        </div>
      )}

      {/* Punch Note Modal */}
      {punchModalFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-bardin-ink">
                📋 Punch List — {punchModalFor.homeowner_name}
              </h3>
              <p className="text-xs text-slate-500">
                {formatAddr(punchModalFor)} · Built {punchModalFor.build_completed_date ? format(new Date(punchModalFor.build_completed_date), "MMM d") : "—"}
              </p>
            </div>
            <div className="p-4 space-y-3">
              {punchModalFor.punchNote && (
                <div className="p-2 bg-red-50 rounded text-xs text-red-700">
                  Existing: {punchModalFor.punchNote}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Add / Update Punch List Notes
                </label>
                <textarea
                  value={punchInput}
                  onChange={(e) => setPunchInput(e.target.value)}
                  rows={3}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  placeholder="Describe items found during walkthrough…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Responsibility</label>
                <select
                  value={punchResponsibility}
                  onChange={(e) => setPunchResponsibility(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                >
                  <option>Bardin pays</option>
                  <option>RM-caused (misc credit)</option>
                  <option>Not our issue</option>
                  <option>Crew to fix</option>
                </select>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setPunchModalFor(null)}
                className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={savePunchNote}
                className="px-4 py-1.5 rounded bg-bardin-teal text-white text-sm hover:opacity-90"
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-3 text-center">
      <div className={"text-xl font-extrabold " + (color ?? "text-bardin-ink")}>{value}</div>
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function FollowupCard({
  build: b,
  routeNum,
  mode,
  expanded,
  onToggleExpand,
  onAddToRoute,
  onRemoveFromRoute,
  onMarkDone,
  onUndo,
  onOpenPunch,
  punchNote,
  suppNotesDraft,
  onSetSuppNotes,
  onSaveSuppNotes,
  onReleaseToShannon,
  releasing,
}: {
  build: RecentBuild;
  routeNum?: number;
  mode: "route" | "available" | "visited";
  expanded: boolean;
  onToggleExpand: () => void;
  onAddToRoute: () => void;
  onRemoveFromRoute: () => void;
  onMarkDone: () => void;
  onUndo?: () => void;
  onOpenPunch: () => void;
  punchNote: string;
  suppNotesDraft?: string;
  onSetSuppNotes?: (v: string) => void;
  onSaveSuppNotes?: () => void;
  onReleaseToShannon?: () => void;
  releasing?: boolean;
}) {
  const isRoute = mode === "route";
  const isVisited = mode === "visited";
  const isAvail = mode === "available";
  const trades = b.trades_to_perform ?? [];

  return (
    <div
      className={
        "rounded-lg border p-3 mb-2 transition-all " +
        (isRoute ? "border-teal-300 bg-teal-50/50" : "") +
        (isVisited ? "opacity-50 border-slate-200" : "") +
        (isAvail ? "border-slate-200 bg-white hover:shadow-sm" : "")
      }
    >
      <div
        className={"flex items-start gap-2 cursor-pointer"}
        onClick={onToggleExpand}
      >
        {isRoute && routeNum != null && (
          <div className="w-6 h-6 rounded-full bg-teal-500 text-white text-[11px] font-extrabold flex items-center justify-center shrink-0 mt-0.5">
            {routeNum}
          </div>
        )}
        {isVisited && <span className="text-lg shrink-0">✅</span>}
        {isAvail && (
          <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-500 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
            +
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm text-bardin-ink">
              <DealLink id={b.id}>{b.homeowner_name ?? "(no name)"}</DealLink>
            </span>
            {punchNote && !isVisited && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                Punch
              </span>
            )}
            {!b.tradesComplete && !isVisited && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                Trades pending
              </span>
            )}
            <span className="text-[10px] text-slate-400 ml-auto">{b.daysAgo}d ago</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {isAddressLinkable(b) ? (
              <a href={mapsHref(b)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline hover:text-slate-700">{formatAddr(b)}</a>
            ) : (
              formatAddr(b)
            )} · RM: {b.rmName}
            {b.claim_number && <> · #{b.claim_number}</>}
          </div>
        </div>
        {isVisited && onUndo && (
          <button
            onClick={(e) => { e.stopPropagation(); onUndo(); }}
            className="shrink-0 px-2 py-1 rounded text-[11px] font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100"
          >
            Undo
          </button>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs mb-2">
            <Detail label="Built" value={b.build_completed_date ? format(new Date(b.build_completed_date), "MMM d") : "—"} />
            <Detail label="Shingle" value={b.shingle_color ?? "—"} />
            <Detail label="Phone" value={formatPhone(b.homeowner_phone) || "—"} />
            <Detail label="Trades" value={trades.length > 0 ? trades.map((t: any) => typeof t === "string" ? t : t.trade).join(", ") : "None"} />
          </div>

          {punchNote && (
            <div className="p-2 bg-red-50 rounded text-xs text-red-700 mb-2">
              📋 <strong>Punch List:</strong> {punchNote}
            </div>
          )}

          {/* Ship 2 (updated May 7): Supplement notes + Notify Shannon — for ready_for_supps deals.
              roof_complete stage retired; build-complete now sends straight to ready_for_supps.
              72h SLA with reminder texts via Bardin Team line at 24/48/72h. SLA breach pings Greg.
              See spec §10c #1 and §10b. */}
          {b.stage === "ready_for_supps" && (
            <SupplementReleaseSection
              build={b}
              draft={suppNotesDraft}
              onSetNotes={onSetSuppNotes}
              onSaveNotes={onSaveSuppNotes}
              onRelease={onReleaseToShannon}
              releasing={releasing}
            />
          )}
          {/* Already-released — show released chip + allow notes editing */}
          {(b.stage === "ready_for_supps" ||
            b.stage === "ready_for_updated_invoice" ||
            b.stage === "supps_waiting_on_final_invoice" ||
            b.stage === "supps_waiting_on_claims") && (
            <SuppNotesEditSection
              build={b}
              draft={suppNotesDraft}
              onSetNotes={onSetSuppNotes}
              onSaveNotes={onSaveSuppNotes}
            />
          )}

          <div className="flex flex-wrap gap-2">
            {isAvail && (
              <button
                onClick={(e) => { e.stopPropagation(); onAddToRoute(); }}
                className="px-3 py-1.5 rounded bg-teal-500 text-white text-xs font-medium hover:bg-teal-600"
              >
                + Add to Route
              </button>
            )}
            {isRoute && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); onMarkDone(); }}
                  className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
                >
                  ✅ Done — Visited
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onRemoveFromRoute(); }}
                  className="px-3 py-1.5 rounded bg-slate-100 text-slate-600 text-xs hover:bg-slate-200"
                >
                  Remove
                </button>
              </>
            )}
            <a
              href={mapsHref(b)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-xs hover:bg-slate-200"
            >
              📍 Navigate
            </a>
            <DealCallText dealId={b.id} homeownerName={b.homeowner_name} homeownerPhone={b.homeowner_phone} />
            <button
              onClick={(e) => { e.stopPropagation(); onOpenPunch(); }}
              className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-xs hover:bg-slate-200"
            >
              📋 Add Punch Note
            </button>
            <DealLink id={b.id}>
              <span
                onClick={(e) => e.stopPropagation()}
                className="px-3 py-1.5 rounded bg-slate-100 text-slate-700 text-xs hover:bg-slate-200 inline-block cursor-pointer"
              >
                View Deal →
              </span>
            </DealLink>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-slate-400">{label}: </span>
      <span className="text-slate-700 font-medium">{value}</span>
    </div>
  );
}

/**
 * Post-release notes editor — shows on cards that have already been released
 * to Shannon (ready_for_supps and beyond). Lets Chad edit supplement notes
 * without re-triggering the release flow.
 */
function SuppNotesEditSection({
  build: b,
  draft,
  onSetNotes,
  onSaveNotes,
}: {
  build: RecentBuild;
  draft?: string;
  onSetNotes?: (v: string) => void;
  onSaveNotes?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const current = b.chad_supplement_notes ?? "";
  const draftVal = draft !== undefined ? draft : current;
  const isDirty = draft !== undefined && draft !== current;

  function handleOpen(e: React.MouseEvent) {
    e.stopPropagation();
    onSetNotes?.(current);
    setEditing(true);
  }
  function handleSave(e: React.MouseEvent) {
    e.stopPropagation();
    onSaveNotes?.();
    setEditing(false);
  }
  function handleCancel(e: React.MouseEvent) {
    e.stopPropagation();
    setEditing(false);
  }

  const STAGE_LABEL: Record<string, string> = {
    ready_for_supps: "Released to Shannon",
    ready_for_updated_invoice: "Awaiting updated invoice",
    supps_waiting_on_final_invoice: "Waiting on final invoice",
    supps_waiting_on_claims: "Waiting on claims",
  };

  return (
    <div className="mt-3 mb-2 p-3 rounded-lg border border-amber-200 bg-amber-50/40">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <span className="text-[11px] font-bold uppercase tracking-wide text-amber-700">
          📤 {STAGE_LABEL[b.stage ?? ""] ?? "Released"}
          {(b as any).chad_supp_notes_due_at && b.stage === "ready_for_supps" && (
            <span className="font-normal ml-1 text-amber-600 normal-case tracking-normal">
              · SLA started {format(new Date((b as any).chad_supp_notes_due_at).getTime() - 72 * 3600 * 1000, "MMM d 'at' h:mm a")}
            </span>
          )}
        </span>
        {!editing && (
          <button
            onClick={handleOpen}
            className="text-[11px] font-semibold text-bardin-orange hover:text-bardin-orange/80 transition"
          >
            📝 {current ? "Edit notes" : "Add notes"}
          </button>
        )}
      </div>

      {!editing && current && (
        <div className="text-xs text-slate-700 bg-white rounded border-l-2 border-bardin-orange pl-2 py-1 pr-1">
          {current}
        </div>
      )}
      {!editing && !current && (
        <p className="text-xs text-slate-400 italic">No supplement notes yet — tap Add notes above.</p>
      )}

      {editing && (
        <>
          <textarea
            value={draftVal}
            onChange={(e) => { e.stopPropagation(); onSetNotes?.(e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            rows={3}
            placeholder="Supplement notes — scope details, Apex contact, outstanding items…"
            className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 font-sans"
            autoFocus
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleSave}
              disabled={!isDirty}
              className={
                "px-3 py-1.5 rounded text-xs font-medium " +
                (isDirty ? "bg-bardin-orange text-white hover:opacity-90" : "bg-slate-100 text-slate-400 cursor-not-allowed")
              }
            >
              💾 Save
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 rounded text-xs border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Ship 2 (updated May 7) — Chad's supplement-notes textarea + Notify Shannon button.
 * Shown on stage='ready_for_supps' cards. Notes save independently of notify;
 * notify additionally starts the 72h SLA timer + fires notifyShannonOnSupplementRelease.
 */
function SupplementReleaseSection({
  build: b,
  draft,
  onSetNotes,
  onSaveNotes,
  onRelease,
  releasing,
}: {
  build: RecentBuild;
  draft?: string;
  onSetNotes?: (v: string) => void;
  onSaveNotes?: () => void;
  onRelease?: () => void;
  releasing?: boolean;
}) {
  const value = draft !== undefined ? draft : (b.chad_supplement_notes ?? "");
  const isDirty = draft !== undefined && draft !== (b.chad_supplement_notes ?? "");
  const canRelease = value.trim().length >= 10;

  return (
    <div className="mt-3 mb-2 p-3 rounded-lg border border-orange-200 bg-orange-50/40">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-orange-700">
          Supplement notes for Shannon
        </span>
        <span className="text-[10px] text-slate-500">
          ⏰ 72h SLA starts on Release · reminders via Bardin Team line at 24/48/72h
        </span>
      </div>
      <textarea
        value={value}
        onChange={(e) => onSetNotes?.(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        placeholder="What did you find on the build follow-up? What should Shannon supplement for? (≥10 chars required for release)"
        rows={3}
        className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 font-sans"
      />
      <div className="flex flex-wrap gap-2 mt-2 items-center">
        <button
          onClick={(e) => { e.stopPropagation(); onSaveNotes?.(); }}
          disabled={!isDirty}
          className={
            "px-3 py-1.5 rounded text-xs font-medium " +
            (isDirty ? "bg-slate-200 text-slate-700 hover:bg-slate-300" : "bg-slate-100 text-slate-400 cursor-not-allowed")
          }
        >
          💾 Save notes
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onRelease?.(); }}
          disabled={!canRelease || !!releasing}
          className={
            "px-3 py-1.5 rounded text-xs font-medium " +
            (canRelease && !releasing
              ? "bg-bardin-orange text-white hover:opacity-90"
              : "bg-slate-200 text-slate-500 cursor-not-allowed")
          }
        >
          {releasing ? "Notifying…" : canRelease ? "Notify Shannon →" : "Notify (need 10+ chars)"}
        </button>
        {value.length > 0 && value.length < 10 && (
          <span className="text-[10px] text-amber-700">{10 - value.length} more chars to release</span>
        )}
      </div>
    </div>
  );
}
